import { datadogRum } from '@datadog/browser-rum'
import {
  type IMutableContext,
  InMemoryStorageProvider,
  UnleashClient,
} from 'unleash-proxy-client'

import { getFlagsConfig } from '~/shell/config'

/** Same-origin path proxied by nginx and Vite to avoid client-side request blocking. */
const UNLEASH_PATH = '/unleash/api/frontend'

/** Names this client in Unleash's own metrics and in the SDK request headers. */
const APP_NAME = 'atlas-frontend'

/**
 * How long the suite waits for toggles before rendering without them.
 *
 * This bounds rendering but does not abort the request. A hung request emits no client event.
 */
const READY_TIMEOUT_MS = 3000

let client: UnleashClient | undefined
let reportedMissingConfig = false

/** Reports failures that would otherwise look like every flag being off. */
const reportFlagFailure = (reason: string, error?: unknown): void => {
  datadogRum.addError(
    error instanceof Error ? error : new Error(`Feature flags: ${reason}`),
    { flags: { reason } },
  )
}

/**
 * Builds the client for this session's context, or nothing when no client key is configured.
 *
 * Passing context at construction ensures it reaches the initial fetch. The singleton keeps that
 * session context stable for the page load (Atlas ADR-0028).
 */
const createClient = (context: IMutableContext): UnleashClient | undefined => {
  const config = getFlagsConfig()
  if (!config) {
    if (!reportedMissingConfig) {
      reportedMissingConfig = true
      reportFlagFailure('no client key in runtime config')
    }
    return undefined
  }

  const created = new UnleashClient({
    appName: APP_NAME,
    clientKey: config.clientKey,
    context,
    // No polling. Every flag's value is read once per session (see `useFlag`), so a refresh could only
    // change a value nothing would re-read, while costing a request per interval for the session's life.
    refreshInterval: 0,
    storageProvider: new InMemoryStorageProvider(),
    url: `${window.location.origin}${UNLEASH_PATH}`,
  })

  // Keep the listener for the client's life because the SDK otherwise swallows later failures.
  created.on('error', (error: unknown) => {
    reportFlagFailure('fetch failed', error)
  })

  return created
}

/**
 * Starts the client and resolves when flags settle or the render timeout expires.
 *
 * Wait on `start()`, not `ready`: `ready` fires only after the client's first successful fetch. Missing
 * configuration resolves immediately with flags off (Atlas ADR-0028).
 */
export const startFlagClient = async (
  context: IMutableContext,
): Promise<void> => {
  client ??= createClient(context)
  const startingClient = client
  if (!startingClient) {
    return
  }

  let timeout: number | undefined
  const timedOut = new Promise<void>((resolve) => {
    timeout = window.setTimeout(() => {
      reportFlagFailure(`no response within ${READY_TIMEOUT_MS}ms`)
      resolve()
    }, READY_TIMEOUT_MS)
  })

  // A rejection must release the rendering gate.
  const starting = startingClient.start().catch((error: unknown) => {
    reportFlagFailure('start failed', error)
  })

  await Promise.race([starting, timedOut])

  // Cleared however the race was won, or a successful load would still report an outage three seconds later.
  window.clearTimeout(timeout)
}

/** Stops the client's metrics timer. Called when the tree that started it unmounts. */
export const stopFlagClient = (): void => {
  client?.stop()
}

/**
 * Whether the named flag is on for this session, or `false` before the client has any toggles.
 *
 * Unresolved flags behave as unreleased features rather than crashing the suite.
 */
export const isFlagEnabled = (name: string): boolean =>
  client?.isEnabled(name) ?? false
